import React, { useMemo } from "react";
import { AnalysisResult, ActionableAdvice } from "../../contracts/scoring";
import { useGuitarMatch } from "../../context/GuitarMatchContext";
import { MetricsChart } from "../RadarChart";
import { Celebration } from "../Celebration";
import {
  METRIC_GOVERNANCE_POLICY,
  isMetricActive,
} from "../../services/tabUnlockPolicy";
import { getVisibleTabsForLevel } from "../../services/viewPolicy";
import { getLockReason } from "../../policy/uiDataPolicy";
import {
  buildCoachLevelTraceReport,
  downloadTestReport,
} from "../../services/testReportService";
import { AdviceCard } from "./AdviceCard";
import { MATCH_REPORT_COPY } from "../../reports/matchReport.copy";

export const InitialReport: React.FC<{
  result: AnalysisResult;
  onReplay: () => void;
  onCompare: (tab?: string) => void;
}> = ({ result, onReplay, onCompare }) => {
  const { currentLevelId, lang, setActiveMission } = useGuitarMatch();
  const isFr = lang === "fr";

  const integrityStatus = result?.session?.truth_report?.integrity_status; // PASS/FAIL/UNVERIFIABLE
  const calibrationStatus = result?.session?.truth_report?.calibration_status; // VALID/INVALID/...
  const isPass = integrityStatus === "PASS";

  // ✅ score fallback (certaines versions: score=null mais match_score présent)
  const scoreRaw =
    (typeof result?.session?.score === "number" ? result.session.score : null) ??
    (typeof (result?.session as any)?.match_score === "number"
      ? (result.session as any).match_score
      : null) ??
    (typeof (result?.session?.metrics as any)?.match_score === "number"
      ? (result.session.metrics as any).match_score
      : null) ??
    null;

  const score = isPass && typeof scoreRaw === "number" ? Math.round(scoreRaw) : null;

  const summary = result?.feedback?.aiCoachSummary;
  const metrics = result?.session?.metrics;

  const isPerfect = score !== null && score >= 95 && integrityStatus === "PASS";

  const certificationLabel = (() => {
    if (integrityStatus === "PASS") return isFr ? "✓ ANALYSE VÉRIFIÉE" : "✓ VERIFIED ANALYSIS";
    if (integrityStatus === "UNVERIFIABLE")
      return isFr ? "⚠ ANALYSE NON VÉRIFIABLE" : "⚠ UNVERIFIABLE ANALYSIS";
    return isFr ? "⛔ ANALYSE REFUSÉE" : "⛔ ANALYSIS REJECTED";
  })();

  const certificationColor =
    integrityStatus === "PASS"
      ? "text-emerald-400"
      : integrityStatus === "UNVERIFIABLE"
      ? "text-orange-400"
      : "text-red-400";

  const defaultInsight = isFr
    ? "Analyse multi-dimensionnelle terminée. Tes constantes vitales de jeu sont prêtes."
    : "Multi-dimensional analysis complete. Your playing vitals are ready.";

  /**
   * ✅ Visible tabs = source de vérité (même logique que CoachSeverityEngine)
   */
  const visibleTabs = useMemo(() => {
    const analysisCount = parseInt(localStorage.getItem("gsc_analysis_count") || "0");
    const isPremium = localStorage.getItem("gsc_is_premium") === "true";
    return getVisibleTabsForLevel(currentLevelId, analysisCount, isPremium);
  }, [currentLevelId]);

  /**
   * Domain -> TabId (doit matcher CoachSeverityEngine + TabRouter)
   */
  const domainToTabId: Record<string, string> = {
    TIMING: "rhythm",
    HARMONY: "harmony",
    STABILITY: "synchronization",
    POSTURE: "posture",
    NUANCE: "nuance",
    TONE: "tone",
    CLEANLINESS: "cleanliness",
    ATTAQUE: "articulation",
    GENERAL: "audio",
  };

  const tabLabel = (tabId: string) => {
    const mapFr: Record<string, string> = {
      rhythm: "Match (Rythme)",
      harmony: "Accords",
      cleanliness: "Propreté",
      synchronization: "Finesse",
      articulation: "Attaque",
      tone: "Timbre",
      nuance: "Nuances",
      posture: "Posture",
      audio: "Audio Lab",
      match: "Match (Replay)",
    };
    const mapEn: Record<string, string> = {
      rhythm: "Timing Match",
      harmony: "Chords",
      cleanliness: "Cleanliness",
      synchronization: "Finesse",
      articulation: "Attack",
      tone: "Tone",
      nuance: "Nuance",
      posture: "Posture",
      audio: "Audio Lab",
      match: "Match (Replay)",
    };
    return (isFr ? mapFr : mapEn)[tabId] || tabId;
  };

  /**
   * ✅ Tab "reportable"
   * - visibleTabs (progression/premium)
   * - isMetricActive (minLevel + calibration)
   * - lockReason null (preuve DSP OK)
   */
  const isTabReportable = (tabId: string) => {
    if (!visibleTabs.includes(tabId)) return false;
    if (!isMetricActive(tabId, currentLevelId, calibrationStatus as any)) return false;
    const reason = getLockReason(tabId, result);
    return reason === null;
  };

  const calibrationBlockedTabs = useMemo(() => {
    return visibleTabs.filter((tabId) => getLockReason(tabId, result) === "CALIBRATION_REQUIRED");
  }, [visibleTabs, result]);

  const levelLockedTabs = useMemo(() => {
    return visibleTabs.filter((tabId) => getLockReason(tabId, result) === "LEVEL_LOCKED");
  }, [visibleTabs, result]);

  const missingDspTabs = useMemo(() => {
    return visibleTabs.filter((tabId) => getLockReason(tabId, result) === "MISSING_DSP_METRICS");
  }, [visibleTabs, result]);

  const reportDiagnostics = useMemo(() => {
    const all: ActionableAdvice[] =
      (((result?.feedback?.diagnostics || []) as any) as ActionableAdvice[]) || [];
    return all.filter((adv) => {
      const tabId = domainToTabId[adv.domain];
      if (!tabId) return false;
      return isTabReportable(tabId);
    });
  }, [result, visibleTabs, currentLevelId, calibrationStatus]);

  const topTips = useMemo(() => {
    const severityOrder: Record<string, number> = { CRITICAL: 0, WARNING: 1, SUCCESS: 2 };
    const sorted = [...reportDiagnostics].sort((a, b) => {
      const av = severityOrder[a.severity] ?? 99;
      const bv = severityOrder[b.severity] ?? 99;
      return av - bv;
    });
    return sorted.filter((d) => d.severity !== "SUCCESS").slice(0, 3);
  }, [reportDiagnostics]);

  const tipsByTab = useMemo(() => {
    const out: Record<string, ActionableAdvice[]> = {};
    for (const adv of reportDiagnostics) {
      const tabId = domainToTabId[adv.domain];
      if (!tabId) continue;
      if (!out[tabId]) out[tabId] = [];
      out[tabId].push(adv);
    }

    const severityOrder: Record<string, number> = { CRITICAL: 0, WARNING: 1, SUCCESS: 2 };
    Object.keys(out).forEach((k) => {
      out[k].sort(
        (a, b) => (severityOrder[a.severity] ?? 99) - (severityOrder[b.severity] ?? 99)
      );
    });

    return out;
  }, [reportDiagnostics]);

  const BreakdownItem = ({
    tabId,
    label,
    val,
  }: {
    tabId: string;
    label: string;
    val: number | null;
  }) => {
    if (!isTabReportable(tabId)) return null;

    const displayVal = isPass && val !== null ? Math.round(val) : null;

    return (
      <div
        onClick={() => onCompare(tabId)}
        className="flex justify-between items-center py-2.5 border-b border-white/5 last:border-0 group cursor-pointer hover:bg-white/5 px-2 -mx-2 rounded-lg"
      >
        <span className="text-[10px] font-black uppercase tracking-widest text-white truncate">
          {label}
        </span>
        <span
          className={`text-sm font-black italic ${
            displayVal !== null && displayVal >= 80 ? "text-emerald-400" : "text-smart-gold"
          }`}
        >
          {displayVal !== null ? `${displayVal}%` : "—"}
        </span>
      </div>
    );
  };

  const minLevelFor = (tabId: string) => METRIC_GOVERNANCE_POLICY[tabId]?.minLevel ?? 1;

  // Narrative copy selector (FR/EN)
  const introCopy = isFr ? MATCH_REPORT_COPY.intro.fr : MATCH_REPORT_COPY.intro.en;
  const prioritiesCopy = isFr ? MATCH_REPORT_COPY.priorities.fr : MATCH_REPORT_COPY.priorities.en;
  const reliabilityNotice = isFr
    ? MATCH_REPORT_COPY.reliabilityNotice.fr
    : MATCH_REPORT_COPY.reliabilityNotice.en;
  const tabTransition = isFr ? MATCH_REPORT_COPY.tabTransition.fr : MATCH_REPORT_COPY.tabTransition.en;

  // Optional: safe accessors (si copy incomplet)
  const globalScoreInsightFn = (MATCH_REPORT_COPY as any).globalScoreInsight || null;
  const tipsIntroFn = (MATCH_REPORT_COPY as any).tipsIntro || null;

  return (
    <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6 p-4 md:p-6 h-full overflow-y-auto">
      <Celebration active={isPerfect} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          <div className="p-8 rounded-[48px] bg-white/[0.03] border border-white/10 shadow-2xl text-center">
            <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.6em] italic">
              Global Fidelity Index
            </span>

            <div className="mt-4 text-8xl font-black italic text-white">
              {score !== null ? score : "—"}
            </div>

            <div className="mt-3 px-4 py-1.5 rounded-full border border-white/10 bg-black/40 inline-block">
              <span className={`text-[8px] font-black uppercase tracking-widest ${certificationColor}`}>
                {certificationLabel}
              </span>
            </div>

            <p className="mt-4 text-[11px] italic text-slate-400 max-w-xl mx-auto">
              {typeof globalScoreInsightFn === "function"
                ? globalScoreInsightFn({ lang, score, integrityStatus })
                : isFr
                ? "Lis ce score comme un thermomètre : il indique où concentrer ton énergie, pas ta valeur de musicien."
                : "Read this score like a thermometer: it shows where to focus your energy, not your worth as a musician."}
            </p>

            {/* ✅ CTA: remettre Match au centre (avant même de corriger la vidéo) */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => onCompare("match")}
                className="px-5 py-2.5 rounded-full bg-white text-black text-[9px] font-black uppercase tracking-widest hover:scale-[1.02] transition-all"
              >
                {isFr ? "Revoir la perf (Match)" : "Replay performance (Match)"}
              </button>

              <button
                onClick={() => onCompare("audio")}
                className="px-5 py-2.5 rounded-full border border-white/10 bg-black/40 text-slate-300 text-[9px] font-black uppercase tracking-widest hover:bg-white hover:text-black transition-all"
              >
                {isFr ? "Comparateur (Vidéos + WAV)" : "Comparator (Videos + WAV)"}
              </button>
            </div>

            <div className="mt-8 bg-black/40 rounded-[32px] p-6">
              <h4 className="text-[8px] font-black text-slate-500 uppercase tracking-[0.3em] mb-4">
                Technical Vitals
              </h4>

              <div className="grid grid-cols-2 gap-x-6">
                <div>
                  <BreakdownItem tabId="rhythm" label="Match" val={(metrics as any)?.rhythm ?? null} />
                  <BreakdownItem tabId="harmony" label="Accords" val={(metrics as any)?.harmony ?? null} />
                  <BreakdownItem tabId="cleanliness" label="Propreté" val={(metrics as any)?.cleanliness ?? null} />
                </div>
                <div>
                  <BreakdownItem
                    tabId="synchronization"
                    label="Finesse"
                    val={(metrics as any)?.synchronization ?? null}
                  />
                  <BreakdownItem
                    tabId="articulation"
                    label="Attaque"
                    val={(metrics as any)?.articulation ?? null}
                  />
                  <BreakdownItem tabId="tone" label="Timbre" val={(metrics as any)?.tone ?? null} />
                </div>
              </div>
            </div>

            <div className="mt-4 text-[10px] text-slate-500 italic">
              {isFr
                ? `Le report suit les onglets visibles et actifs (niveau + calibration).`
                : `This report follows visible + active tabs (level + calibration).`}
            </div>
          </div>

          <MetricsChart
            metrics={metrics as any}
            overallScore={score as any}
            lang={lang}
            levelId={currentLevelId}
          />
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          <div className="flex-1 bg-black/40 rounded-[48px] border border-white/10 p-8 space-y-6">
            <h3 className="text-[10px] font-black uppercase tracking-[0.5em] italic text-smart-accent">
              Neural Briefing
            </h3>

            <p className="text-2xl font-bold italic text-white">
              “{summary?.insight || defaultInsight}”
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() =>
                  buildCoachLevelTraceReport(result).then(downloadTestReport).catch(() => {})
                }
                className="px-4 py-2 rounded-full border border-white/10 text-[8px] font-black uppercase tracking-widest text-slate-400 hover:bg-white hover:text-black transition-all"
              >
                Audit JSON
              </button>
            </div>

            {summary?.exercise && (
              <div
                onClick={() => {
                  setActiveMission((summary as any).exercise);
                  onReplay();
                }}
                className="mt-2 p-5 rounded-[32px] bg-smart-gold/10 border border-smart-gold/20 cursor-pointer"
              >
                <span className="text-[9px] font-black uppercase tracking-[0.4em] text-smart-gold">
                  {isFr ? "Action de Progrès" : "Progress Action"}
                </span>
                <h4 className="text-lg font-black italic text-white">
                  {(summary as any).exercise?.name}
                </h4>
              </div>
            )}
          </div>

          {/* ✅ MATCH REPORT — ALL TIPS */}
          <div className="bg-white/[0.03] border border-white/10 rounded-[48px] p-8 space-y-6">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-[10px] font-black uppercase tracking-[0.5em] italic text-white">
                {isFr ? "Match Report — Conseils" : "Match Report — Tips"}
              </h3>
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                {isFr ? "Basé sur les onglets actifs" : "Based on active tabs"}
              </span>
            </div>

            <p className="text-[12px] italic text-slate-400 max-w-2xl">
              {typeof tipsIntroFn === "function"
                ? tipsIntroFn({
                    lang,
                    topTipsCount: topTips.length,
                    totalTipsCount: reportDiagnostics.length,
                  })
                : isFr
                ? "Commence par les 3 priorités : ce sont les leviers qui donnent le plus de progression pour le moins d’effort."
                : "Start with the top 3 priorities: they are the highest-progress levers for the least effort."}
            </p>

            <div className="p-6 rounded-[32px] bg-black/40 border border-white/10 space-y-3">
              <p className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-500">
                {introCopy.title}
              </p>
              <p className="text-sm italic text-slate-200/90 leading-relaxed">
                {introCopy.text}
              </p>
              <p className="text-xs text-slate-400 italic">{tabTransition}</p>
            </div>

            {(levelLockedTabs.length > 0 || missingDspTabs.length > 0) && (
              <div className="p-5 rounded-[32px] border border-white/10 bg-black/30 space-y-3">
                <p className="text-xs italic text-slate-300">{reliabilityNotice}</p>

                {levelLockedTabs.length > 0 && (
                  <div className="pt-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {isFr ? "Niveau requis" : "Required level"}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {levelLockedTabs.map((tabId) => (
                        <span
                          key={tabId}
                          className="px-3 py-1 rounded-full bg-black/40 border border-white/10 text-[8px] font-black uppercase tracking-widest text-slate-400"
                        >
                          {tabLabel(tabId)} — L{minLevelFor(tabId)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {missingDspTabs.length > 0 && (
                  <div className="pt-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {isFr ? "Mesures absentes" : "Missing measures"}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {missingDspTabs.map((tabId) => (
                        <span
                          key={tabId}
                          className="px-3 py-1 rounded-full bg-black/40 border border-white/10 text-[8px] font-black uppercase tracking-widest text-slate-400"
                        >
                          {tabLabel(tabId)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {calibrationBlockedTabs.length > 0 && (
              <div className="p-5 rounded-[32px] border-2 border-orange-500/40 bg-orange-500/10">
                <p className="text-[9px] font-black uppercase tracking-widest text-orange-400">
                  {isFr ? "Calibration requise" : "Calibration required"}
                </p>
                <p className="text-[10px] mt-2 italic text-orange-200/80">
                  {isFr
                    ? "Certains modules sont visibles et ont des données, mais leurs conseils sont bloqués car la calibration n’est pas VALID."
                    : "Some modules are visible and have data, but their tips are blocked because calibration isn’t VALID."}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {calibrationBlockedTabs.map((tabId) => (
                    <span
                      key={tabId}
                      className="px-3 py-1 rounded-full bg-black/40 border border-orange-500/30 text-[8px] font-black uppercase tracking-widest text-orange-300"
                    >
                      {tabLabel(tabId)}
                    </span>
                  ))}
                </div>
                <div className="mt-3 text-[10px] italic text-orange-100/70">
                  {isFr
                    ? "Action : relance une calibration pour débloquer ces conseils."
                    : "Action: run calibration to unlock these tips."}
                </div>
              </div>
            )}

            <div className="p-5 rounded-[32px] bg-white/[0.02] border border-white/10">
              <p className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-500">
                {prioritiesCopy.title}
              </p>
              <p className="text-xs italic text-slate-300 mt-2">{prioritiesCopy.text}</p>
            </div>

            <div className="space-y-3">
              {topTips.length > 0 ? (
                topTips.map((adv, i) => (
                  <AdviceCard key={`top-${adv.id || i}`} advice={adv} isHighContrast={false} />
                ))
              ) : (
                <div className="p-6 rounded-[32px] bg-emerald-500/5 border border-emerald-500/20 text-center">
                  <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">
                    {isFr ? "Aucun point critique détecté" : "No critical issues detected"}
                  </span>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-white/10 space-y-8">
              {Object.keys(tipsByTab).length === 0 ? (
                <div className="p-6 text-center opacity-50">
                  <p className="text-xs font-bold uppercase text-slate-500">
                    {isFr ? "Aucun conseil disponible" : "No tips available"}
                  </p>
                </div>
              ) : (
                Object.entries(tipsByTab).map(([tabId, advs]) => {
                  if (!isTabReportable(tabId)) return null;

                  return (
                    <div key={tabId} className="space-y-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <span className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-500">
                            {tabLabel(tabId)}
                          </span>
                          <span className="text-[8px] font-black uppercase tracking-widest text-slate-600">
                            {advs.length} tip{advs.length > 1 ? "s" : ""}
                          </span>
                          <span className="text-[8px] font-black uppercase tracking-widest text-slate-700">
                            L{minLevelFor(tabId)}+
                          </span>
                        </div>

                        <button
                          onClick={() => onCompare(tabId)}
                          className="px-4 py-2 rounded-full border border-white/10 text-[8px] font-black uppercase tracking-widest text-slate-400 hover:bg-white hover:text-black transition-all"
                        >
                          {isFr ? "Voir l’onglet" : "Open tab"}
                        </button>
                      </div>

                      <div className="space-y-3">
                        {advs.map((adv, i) => (
                          <AdviceCard
                            key={`${tabId}-${adv.id || i}`}
                            advice={adv}
                            isHighContrast={false}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};